import { EventEmitter } from 'events';
import uuid from "uuid/v4";

export interface WSMsg {
    command: string;
    requestID: string;
    data: any;
}

export class WebsocketClient extends EventEmitter {
    ws: WebSocket;

    reconnecting = false;
    connecting = false;
    connected = false;
    disabled = false;
    reconnectAttempts = 0;

    packetQueue: any[] = [];
    packetCallbacks: Map<string,{res: Function,rej: Function}>;

    constructor(public frontendClient) {
        super();

        this.packetCallbacks = new Map();
        frontendClient.onceLoggedIn(() => {
            this.connect();
        });
    }

    connect() {
        if(this.connecting) {return;}
        if(this.disabled) {return;}
        if(this.reconnectAttempts > 5) {
            this.log("[ERR] Couldn't reconnect to the server after 5 tries. Giving up... 3:");
            return;
        }

        this.connecting = true;
        this.reconnectAttempts++;

        this.ws = new WebSocket(this.frontendClient.baseWSURL + "/livesync/start")

        this.log("[LOG] Connecting to " + this.ws.url);

        let connectTimerID;

        this.ws.onopen = (ev) => {
            connectTimerID = setTimeout(() => {
                connectTimerID = null;

                if(this.ws.readyState === 1) {
                    if(this.reconnecting) {
                        this.log("[LOG] Now reconnected to " + this.ws.url);
                        this.emit("reconnected");
                    }
                    else {
                        this.log("[LOG] Now connected to " + this.ws.url);
                    }
        
                    this.reconnecting = false;
                    this.connecting = false;
                    this.connected = true;
                    this.reconnectAttempts = 0;
        
                    for(let packet of this.packetQueue) {
                        this.sendPacket(packet);
                    }

                    this.packetQueue.splice(0,this.packetQueue.length);
                }
            },500);
        }

        this.ws.onclose = (ev) => {
            this.connecting = false;
            this.connected = false;

            if(connectTimerID) {clearTimeout(connectTimerID);}

            this.log("[LOG] Now disconnected from " + this.ws.url + " (" + ev.reason + ")");

            setTimeout(() => {
                this.reconnecting = true;
                this.connect();
            },150);
        }

        this.ws.onmessage = (ev) => {
            let msg = ev.data;
            let packet = JSON.parse(msg);
            this.handleIncomingMessage(packet);
        }
    }

    disconnect(reason?: string) {
        this.disabled = true;
        this.ws.close(1000,reason)
    }

    log(msg: string) {
        console.log("[wsLiveSync] " + msg);
    }

    sendPacket(packet: any) {
        if(!this.connected) {
            this.packetQueue.push(packet);
            return;
        }

        this.ws.send(JSON.stringify(packet));
    }

    launchRequest<T>(command: string,data: any) {
        return new Promise<T>((res,rej) => {
            let requestID = uuid();
            this.packetCallbacks.set(requestID,{res,rej});
            this.sendPacket({
                command,
                data,
                requestID,
            });
        });
    }

    handleIncomingMessage(msg: WSMsg) {
        switch(msg.command) {
            case "error": {
                let handlers = this.packetCallbacks.get(msg.requestID);

                if(handlers) {
                    this.packetCallbacks.delete(msg.requestID);
                    handlers.rej(msg.data.message);
                }
                else {
                    this.log("[ERR] Unhandled serverside error: " + msg.data.message);
                    break;
                }
            }
            case "success": {
                let handlers = this.packetCallbacks.get(msg.requestID);

                if(handlers) {
                    this.packetCallbacks.delete(msg.requestID);
                    handlers.res(msg.data);
                }

                break;
            }
            case "updatecal": {
                this.emit("updatecal",msg.data);
                break;
            }
            case "newmessage": {
                this.emit("newmessage",msg.data);
                break;
            }
            case "typing": {
                this.emit("typing",msg.data);
                break;
            }
            case "newmsg": {
                this.emit("newmsg",msg.data);
                break;
            }
            case "newalert": {
                this.emit("newalert",msg.data);
                break;
            }
            default: {
                this.log("[ERR] unknown command from server: " + msg.command)
                return;
            }
        }
    }

    acknowledgePacket(requestID: string,data: any) {
        this.sendPacket({
            command: "success",
            data,
            requestID,
        })
    }
}
